// src/contexts/DAppContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { ethers } from 'ethers';
import { WalletContext } from './WalletContext';
import { NetworkContext } from './NetworkContext';
import walletConnectService from '../services/walletConnectService';
import { injectEthereumProvider } from '../utils/providerUtils';
import { buildAuthObject, populateAuthPayload } from '@walletconnect/utils';
import { Buffer as BufferPolyfill } from 'buffer';

export const DAppContext = createContext(null);

// Storage key for connections
const CONNECTIONS_STORAGE_KEY = 'bitdog_dapp_connections';

// Storage key for connection origins (where to handle requests)
const CONNECTION_ORIGINS_STORAGE_KEY = 'bitdog_connection_origins';

// WalletConnect Project ID 
const PROJECT_ID = 'd092dd40fcb87151ee90a4089d494ead';

export const DAppProvider = ({ children }) => {
  const { currentWallet, provider } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  
  // State for connections
  const [activeConnections, setActiveConnections] = useState([]);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isInjecting, setIsInjecting] = useState(false);
  const [isWalletKitInitialized, setIsWalletKitInitialized] = useState(false);
  
  // Store where each connection should be handled (browser or wallet)
  const [connectionOrigins, setConnectionOrigins] = useState({});

  // Refs for providers
  const injectedProviderRef = React.useRef(null);
  
  // Load connection origins on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONNECTION_ORIGINS_STORAGE_KEY);
      if (saved) {
        setConnectionOrigins(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load connection origins:', error);
    }
  }, []);

  if (typeof window !== 'undefined' && typeof window.Buffer === 'undefined') {
    window.Buffer = BufferPolyfill;
    console.log('Buffer polyfill installed');
  }
  
  // Save connection origins when they change
  useEffect(() => {
    try {
      localStorage.setItem(CONNECTION_ORIGINS_STORAGE_KEY, JSON.stringify(connectionOrigins));
    } catch (error) {
      console.error('Failed to save connection origins:', error);
    }
  }, [connectionOrigins]);
  
  // Initialize WalletKit when wallet or chain changes
  useEffect(() => {
    const initializeWalletKit = async () => {
      if (!currentWallet || !currentChain) {
        console.log('Cannot initialize WalletKit: Missing wallet or chain');
        return;
      }
      
      try {
        // Define metadata for our wallet
        const metadata = {
          name: 'BitDog Wallet',
          description: 'A secure multi-chain wallet',
          url: 'https://bitdogwallet.com',
          icons: ['https://bitdogwallet.com/assets/wallet-logo.png'],
          redirect: {
            native: 'bitdogwallet://'
          }
        };
        
        // Define supported chains
        const supportedChains = [
          {
            id: currentChain.chainId.toString(),
            name: currentChain.name
          }
        ];
        
        console.log('Initializing WalletKit with project ID:', PROJECT_ID);
        console.log('Current wallet:', currentWallet.address);
        console.log('Current chain:', currentChain.key, currentChain.chainId);
        
        // Initialize WalletKit
        const success = await walletConnectService.initialize(
          PROJECT_ID,
          metadata,
          currentWallet,
          supportedChains
        );
        
        if (success) {
          console.log('WalletKit initialized successfully');
          setIsWalletKitInitialized(true);
          
          // Setup callbacks
          walletConnectService.onSessionProposal(handleSessionProposal);
          walletConnectService.onSessionRequest(handleSessionRequest);
          walletConnectService.onAuthRequest(handleAuthRequest);
        } else {
          console.error('Failed to initialize WalletKit - service returned false');
        }
      } catch (error) {
        console.error('Failed to initialize WalletKit:', error);
      }
    };
    
    // If not yet initialized, try to initialize
    if (!isWalletKitInitialized) {
      initializeWalletKit();
    }
  }, [currentWallet, currentChain, isWalletKitInitialized]);
  
  // Load saved connections on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONNECTIONS_STORAGE_KEY);
      if (saved) {
        const connections = JSON.parse(saved);
        setActiveConnections(connections);
      }
    } catch (error) {
      console.error('Failed to load saved connections:', error);
    }
  }, []);
  
  // Save connections when they change
  useEffect(() => {
    try {
      localStorage.setItem(CONNECTIONS_STORAGE_KEY, JSON.stringify(activeConnections));
    } catch (error) {
      console.error('Failed to save connections:', error);
    }
  }, [activeConnections]);

  // Sync active connections with WalletConnect sessions
  useEffect(() => {
    if (!isWalletKitInitialized || !currentWallet) return;
    
    // Sync wallet's active connections with WalletConnect sessions
    const syncWalletConnectSessions = async () => {
      try {
        // Get current WalletConnect sessions
        const wcSessions = walletConnectService.getActiveSessions();
        console.log('Current WC sessions:', wcSessions);
        
        // Get session topics
        const sessionTopics = Object.keys(wcSessions);
        
        if (sessionTopics.length === 0) {
          // If no active WC sessions but we have WC connections in our state,
          // we need to clean them up
          const wcConnections = activeConnections.filter(
            conn => conn.connectionType === 'walletconnect'
          );
          
          if (wcConnections.length > 0) {
            console.log('Cleaning up stale WalletConnect connections');
            setActiveConnections(prev => 
              prev.filter(conn => conn.connectionType !== 'walletconnect')
            );
          }
          return;
        }
        
        // Check if we need to update our active connections
        const currentWcConnections = activeConnections.filter(
          conn => conn.connectionType === 'walletconnect'
        );
        
        // Find missing connections that need to be added
        const missingTopics = sessionTopics.filter(topic => 
          !currentWcConnections.some(conn => conn.topic === topic)
        );
        
        if (missingTopics.length > 0) {
          console.log(`Found ${missingTopics.length} missing WalletConnect sessions to restore`);
          
          // Create connection objects for each missing session
          const newConnections = missingTopics.map(topic => {
            const session = wcSessions[topic];
            const origin = walletConnectService.getSessionOrigin(topic);
            
            return {
              url: session.peer?.metadata?.url || 'Unknown',
              name: session.peer?.metadata?.name || 'Unknown DApp',
              connectionType: 'walletconnect',
              timestamp: Date.now(), // Use current time since we don't know original
              address: currentWallet.address,
              topic: topic,
              // Use proper handler location from service
              wcHandlerLocation: origin
            };
          });
          
          // Update active connections to include the restored sessions
          setActiveConnections(prev => [...prev, ...newConnections]);
          
          console.log('Restored WalletConnect sessions:', newConnections);
        }
        
        // Also check for stale connections (connections without active sessions)
        const staleConnections = currentWcConnections.filter(
          conn => conn.topic && !sessionTopics.includes(conn.topic)
        );
        
        if (staleConnections.length > 0) {
          console.log(`Found ${staleConnections.length} stale WalletConnect connections to remove`);
          
          // Remove stale connections
          setActiveConnections(prev => 
            prev.filter(conn => 
              conn.connectionType !== 'walletconnect' || 
              !conn.topic || 
              sessionTopics.includes(conn.topic)
            )
          );
        }
      } catch (error) {
        console.error('Error syncing WalletConnect sessions:', error);
      }
    };
    
    // Run sync on initialization and when wallet changes
    syncWalletConnectSessions();
    
    // Also run periodically to catch any changes
    const syncInterval = setInterval(syncWalletConnectSessions, 5000); // Check more frequently
    
    return () => clearInterval(syncInterval);
  }, [isWalletKitInitialized, currentWallet, activeConnections]);

  // Handle session proposals from WalletConnect
  const handleSessionProposal = (proposal, wcHandlerLocation = null) => {
    console.log('Handling session proposal:', proposal, 'handler location:', wcHandlerLocation);
    
    // Extract dApp metadata from proposal
    const { proposer } = proposal.params;
    const { name, url } = proposer.metadata;
    
    // Get security context for Verify API
    const verifyContext = proposal.params.verifyContext || {};
    
    // Store where this connection should be handled
    if (wcHandlerLocation) {
      setConnectionOrigins(prev => ({
        ...prev,
        [proposal.id]: wcHandlerLocation
      }));
      
      // Also store in the service
      walletConnectService.setWcHandlerLocation(wcHandlerLocation);
    }
    
    // Create a pending connection with wcHandlerLocation and security info
    setPendingConnection({
      id: proposal.id,
      url,
      name,
      connectionType: 'walletconnect',
      wcHandlerLocation, // Store where this connection should be handled
      securityInfo: {
        validation: verifyContext?.validation || 'UNKNOWN',
        isScam: verifyContext?.isScam || false
      }
    });
  };
  
  // Handle session requests from WalletConnect
  const handleSessionRequest = (requestEvent, wcHandlerLocation = null) => {
    console.log('Handling session request:', requestEvent, 'handler location:', wcHandlerLocation);
    
    try {
      // Extract method and params
      let method, params;
      
      // Check if requestEvent has the expected structure
      if (requestEvent && requestEvent.params && requestEvent.params.request) {
        // Standard format from WalletKit
        method = requestEvent.params.request.method;
        params = requestEvent.params.request.params;
      } 
      // Check for alternative format
      else if (requestEvent && requestEvent.type === 'transaction' && requestEvent.method) {
        // Direct format 
        method = requestEvent.method;
        params = requestEvent.params;
        console.log('Using direct request format:', method, params);
      }
      // Last resort
      else if (requestEvent) {
        console.log('Attempting to parse non-standard request format:', requestEvent);
        method = requestEvent.method;
        params = requestEvent.params;
      }
      
      if (!method) {
        console.error('Invalid session request format - couldn\'t extract method:', requestEvent);
      }
      
      console.log('Extracted request method:', method, 'params:', params);
      
      // Group methods by type
      const transactionMethods = [
        "eth_accounts",
        "eth_requestAccounts",
        "eth_sendRawTransaction",
        "eth_sign",
        "eth_signTransaction",
        "eth_signTypedData",
        "eth_signTypedData_v3",
        "eth_signTypedData_v4",
        "eth_sendTransaction",
        "personal_sign",
        "wallet_switchEthereumChain",
        "wallet_addEthereumChain",
        "wallet_getPermissions",
        "wallet_requestPermissions",
        "wallet_registerOnboarding",
        "wallet_watchAsset",
        "wallet_scanQRCode",
        "wallet_sendCalls",
        "wallet_getCallsStatus",
        "wallet_showCallsStatus",
        "wallet_getCapabilities",
      ];
      const signatureMethods = ['eth_sign', 'personal_sign', 'eth_signTypedData', 'eth_signTypedData_v4'];
      const chainMethods = ['wallet_switchEthereumChain', 'wallet_addEthereumChain'];
      
      let requestType = 'other';
      
      if (transactionMethods.includes(method)) {
        requestType = 'transaction';
      } else if (signatureMethods.includes(method)) {
        requestType = 'signature';
      } else if (chainMethods.includes(method)) {
        requestType = 'chainSwitch';
      }
      
      // Check if this is a permit transaction
      let isPermit = false;
      let isPostPermitSwap = false;
      
      // Special check for eth_sendTransaction
      if (method === 'eth_sendTransaction' && params) {
        // Handle params that might be in different formats
        let txData = null;
        
        // Try to extract data field from params
        if (Array.isArray(params) && params.length > 0 && params[0] && params[0].data) {
          txData = params[0].data;
        } else if (!Array.isArray(params) && params.data) {
          txData = params.data;
        } else if (requestEvent.params && Array.isArray(requestEvent.params) && 
                  requestEvent.params.length > 0 && requestEvent.params[0] && 
                  requestEvent.params[0].data) {
          txData = requestEvent.params[0].data;
        }
        
        // Now check if this data indicates a permit 
        if (txData) {
          isPermit = walletConnectService.isPermitTransaction(txData);
          
          if (isPermit) {
            console.log('PERMIT TRANSACTION DETECTED in request handler!');
          }
        }
      }
      
      // CRITICAL: Force permit transactions to wallet
      let handlerLocation = wcHandlerLocation;
      if (isPermit || isPostPermitSwap) {
        handlerLocation = 'wallet';
      }
      
      // Get the session URL for this request
      let sessionUrl = 'Unknown';
      if (requestEvent.url) {
        sessionUrl = requestEvent.url;
      } else if (requestEvent.topic) {
        sessionUrl = walletConnectService.getSessionUrl(requestEvent.topic);
      }
      
      // Get security context for Verify API
      const verifyContext = requestEvent.verifyContext || {};
      
      // Create a standardized request object with all properties
      const newRequest = {
        id: requestEvent.id,
        topic: requestEvent.topic,
        type: requestType,
        method,
        // Handle different params formats
        params: params || (Array.isArray(requestEvent.params) ? requestEvent.params : []),
        source: 'walletconnect',
        url: sessionUrl,
        timestamp: Date.now(),
        wcHandlerLocation: handlerLocation,
        isPermit,
        isPostPermitSwap,
        securityInfo: {
          validation: verifyContext?.validation || 'UNKNOWN',
          isScam: verifyContext?.isScam || false
        }
      };
      
      console.log(`Adding request to pending requests:`, newRequest);
      
      // Add the request to pending requests with correct handler location
      setPendingRequests(prev => [
        ...prev,
        newRequest
      ]);
      
      console.log(`Added request ${requestEvent.id} to pending requests with handler location: ${handlerLocation}`);
      
      // Session origin tracking (if topic exists)
      if (requestEvent.topic) {
        if (isPermit || isPostPermitSwap) {
          walletConnectService.setSessionOrigin(requestEvent.topic, 'wallet');
          
          // Also update our local state
          setConnectionOrigins(prev => ({
            ...prev,
            [requestEvent.topic]: 'wallet'
          }));
        } else if (handlerLocation) {
          walletConnectService.setSessionOrigin(requestEvent.topic, handlerLocation);
          
          // Also update our local state
          setConnectionOrigins(prev => ({
            ...prev,
            [requestEvent.topic]: handlerLocation
          }));
        }
      }
    } catch (error) {
      console.error('Error processing session request:', error);
      
      // Last resort: Just add the raw request to the queue
      if (requestEvent && requestEvent.id) {
        console.log('Adding raw request to queue as fallback');
        const rawRequest = {
          ...requestEvent,
          type: requestEvent.type || 'transaction',
          source: 'walletconnect',
          timestamp: Date.now(),
          wcHandlerLocation: 'wallet',
        };
        
        setPendingRequests(prev => [...prev, rawRequest]);
      }
    }
  };
  
  // Handle auth requests from WalletConnect
  const handleAuthRequest = (authEvent, wcHandlerLocation = null) => {
    console.log('Handling auth request:', authEvent, 'handler location:', wcHandlerLocation);
    
    // Get security context for Verify API
    const verifyContext = authEvent.verifyContext || {};
    
    // Store where this request should be handled
    if (wcHandlerLocation) {
      setConnectionOrigins(prev => ({
        ...prev,
        [authEvent.id]: wcHandlerLocation
      }));
    }
    
    // Prepare authentication request
    const authRequest = {
      id: authEvent.id,
      type: 'authentication',
      params: authEvent.params,
      source: 'walletconnect',
      url: authEvent.params.metadata?.url || 'Unknown',
      timestamp: Date.now(),
      wcHandlerLocation, // Store where this request should be handled
      securityInfo: {
        validation: verifyContext?.validation || 'UNKNOWN',
        isScam: verifyContext?.isScam || false
      }
    };
    
    // Add to pending requests
    setPendingRequests(prev => [...prev, authRequest]);
  };
  
  // Handle authentication approval
  async function approveAuthentication(authRequest) {
    try {
      console.log('Authentication request received:', authRequest);
      
      // Make sure we have required components
      if (!authRequest || !currentWallet || !provider) {
        console.error('Missing required parameters for authentication', { 
          hasAuthRequest: !!authRequest, 
          hasWallet: !!currentWallet, 
          hasProvider: !!provider 
        });
        return false;
      }
  
      // Create a signer from the wallet's private key
      const signer = new ethers.Wallet(currentWallet.privateKey, provider);
      
      // CRITICAL FIX: Safely extract the message to sign
      let message;
      
      // Check different possible message locations
      if (authRequest.params && authRequest.params.message) {
        message = authRequest.params.message;
        console.log("Found message in authRequest.params.message");
      } 
      else if (authRequest.params && authRequest.params.request && authRequest.params.request.params) {
        // Sometimes the message is nested in request.params
        message = authRequest.params.request.params.message || authRequest.params.request.params[0];
        console.log("Found message in authRequest.params.request.params");
      }
      else if (authRequest.message) {
        message = authRequest.message;
        console.log("Found message in authRequest.message");
      }
      else if (Array.isArray(authRequest.params)) {
        // For some authentication requests, the message might be in the params array
        message = authRequest.params[0];
        console.log("Found message in authRequest.params array");
      }
      
      // If we still couldn't find the message, create a default authentication message
      if (!message) {
        console.warn("No message found in authentication request, using default");
        message = `I am signing this message to authenticate with ${authRequest.url || 'this site'} at ${new Date().toISOString()}`;
      }
      
      console.log('Authentication message to sign:', message);
      
      // Sign the message - handle different message formats
      let signature;
      
      try {
        if (typeof message === 'string') {
          // If message is hex string, convert to bytes
          if (ethers.isHexString(message)) {
            try {
              const messageBytes = ethers.getBytes(message);
              signature = await signer.signMessage(messageBytes);
            } catch (hexError) {
              console.error('Error converting hex message:', hexError);
              // Fallback to direct signing
              signature = await signer.signMessage(message);
            }
          } else {
            // Regular string message
            signature = await signer.signMessage(message);
          }
        } else if (message instanceof Uint8Array) {
          // Already in bytes format
          signature = await signer.signMessage(message);
        } else {
          // Try to stringify the message
          const stringMessage = JSON.stringify(message);
          signature = await signer.signMessage(stringMessage);
        }
        
        console.log('Authentication signature generated:', signature);
      } catch (signError) {
        console.error('Error signing authentication message:', signError);
        return false;
      }
      
      // Build authentication object
      const auth = {
        t: "eip191", // signature type
        s: signature,
        m: message,
      };
      
      // Format the response for WalletConnect
      if (authRequest.topic && walletConnectService) {
        try {
          // For WalletConnect authentication
          const response = {
            id: authRequest.id,
            jsonrpc: "2.0",
            result: [auth]
          };
          
          console.log('Sending authentication response through WalletConnect:', response);
          
          await walletConnectService.respondSessionRequest({
            topic: authRequest.topic,
            response: response
          });
          
          return true;
        } catch (wcError) {
          console.error('Error sending authentication through WalletConnect:', wcError);
          return false;
        }
      } else {
        // For non-WalletConnect authentication (e.g., injected provider)
        try {
          // Here we'd handle authentication for other types of connections
          // This would depend on your implementation
          console.log('Non-WalletConnect authentication not fully implemented');
          return true;
        } catch (error) {
          console.error('Error handling non-WalletConnect authentication:', error);
          return false;
        }
      }
    } catch (error) {
      console.error('Error approving authentication:', error);
      return false;
    }
  }
  
  // Reject authentication
  const rejectAuthentication = async (authRequest) => {
    if (!isWalletKitInitialized) {
      console.error('Cannot reject authentication: WalletKit not initialized');
      return false;
    }
    
    try {
      await walletConnectService.rejectSessionAuthenticate({
        id: authRequest.id,
        reason: 'USER_REJECTED'
      });
      
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(req => req.id !== authRequest.id));
      
      return true;
    } catch (error) {
      console.error('Error rejecting authentication:', error);
      return false;
    }
  };
  
  // Connect to a dApp with walletconnect URI
const connectWithWalletConnectUri = async (uri, wcHandlerLocation = null) => {
  console.log(`Attempting to connect with WalletConnect URI, handler location: ${wcHandlerLocation}`);
  
  // Try to initialize WalletKit if not already initialized
  if (!isWalletKitInitialized) {
    try {
      // Check if we have required values
      if (!currentWallet || !currentChain) {
        console.error('Cannot initialize WalletKit: Missing wallet or chain');
        return false;
      }
      
      // Attempt to initialize WalletKit before proceeding
      const initSuccess = await walletConnectService.reconnect();
      console.log('Forced WalletKit reconnection result:', initSuccess);
      
      // Give it a moment to establish connection
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsWalletKitInitialized(true); // Set to true to enable further operations
    } catch (initError) {
      console.warn('WalletKit forced initialization failed:', initError);
      // Continue anyway, as we'll try to pair regardless
    }
  }
  
  try {
    // Validate URI format
    if (!uri || !uri.startsWith('wc:')) {
      throw new Error('Invalid WalletConnect URI format');
    }
    
    // Before pairing, set the current handler location in the service
    walletConnectService.setWcHandlerLocation(wcHandlerLocation);
    
    console.log('Starting WalletConnect pairing');
    const success = await walletConnectService.pair(uri);
    console.log('WalletConnect pairing result:', success);
    
    return success;
  } catch (error) {
    console.error('Failed to connect with URI:', error);
    return false;
  }
};
  
  // Connect to a dApp via injection
  const connectToDApp = (url, name, wcHandlerLocation = 'browser') => {
    // Check if already connected
    const existingConnection = activeConnections.find(conn => conn.url === url);
    if (existingConnection) {
      console.log(`Already connected to ${url}`);
      return;
    }
    
    // Set up pending connection
    setPendingConnection({
      url,
      name,
      connectionType: 'injected', // Default to injected (can be changed by UI)
      wcHandlerLocation // Browser connections by default
    });
  };
  
  // Approve connection request
  const approveConnection = async () => {
    if (!pendingConnection) return false;
    
    try {
      const { url, name, connectionType, id, wcHandlerLocation } = pendingConnection;
      
      if (connectionType === 'walletconnect' && id) {
        console.log('Approving WalletConnect session:', id);
        
        // Set the handler location before approving
        walletConnectService.setWcHandlerLocation(wcHandlerLocation);
        
        // Approve WalletConnect session
        const session = await walletConnectService.approveSession(id, currentWallet, currentChain.chainId);
        
        // Store the connection origin in the service
        walletConnectService.setSessionOrigin(session.topic, wcHandlerLocation);
        
        // Add to active connections
        const newConnection = {
          url,
          name,
          connectionType,
          timestamp: Date.now(),
          address: currentWallet.address,
          topic: session.topic,
          wcHandlerLocation // Preserve where this connection should be handled
        };
        
        setActiveConnections(prev => [...prev, newConnection]);
        console.log(`Successfully connected to ${url} using WalletConnect`);
        
        // Store the connection origin for later use
        setConnectionOrigins(prev => ({
          ...prev,
          [session.topic]: wcHandlerLocation || 'wallet' // Default to wallet if not specified
        }));
        
        // Clear pending connection
        setPendingConnection(null);
        
        // Show success notification if available
        if (window._connectionSuccessRef && typeof window._connectionSuccessRef.showSuccess === 'function') {
          console.log('Showing connection success notification for:', name, url);
          window._connectionSuccessRef.showSuccess(name, url);
        } else {
          console.log('Connection success notification not available');
        }
        
        return true;
      } else {
        // Add injected connection
        const newConnection = {
          url,
          name,
          connectionType: 'injected',
          timestamp: Date.now(),
          address: currentWallet.address,
          wcHandlerLocation // Preserve where this connection should be handled
        };
        
        setActiveConnections(prev => [...prev, newConnection]);
        console.log(`Successfully connected to ${url} using injected provider`);
        
        // Clear pending connection
        setPendingConnection(null);
        
        // Show success notification if available
        if (window._connectionSuccessRef && typeof window._connectionSuccessRef.showSuccess === 'function') {
          console.log('Showing connection success notification for:', name, url);
          window._connectionSuccessRef.showSuccess(name, url);
        } else {
          console.log('Connection success notification not available');
        }
        
        return true;
      }
    } catch (error) {
      console.error('Connection approval failed:', error);
      setPendingConnection(null);
      return false;
    }
  };
  
  // Reject connection request
  const rejectConnection = async () => {
    const { id, connectionType } = pendingConnection || {};
    
    if (connectionType === 'walletconnect' && id) {
      try {
        await walletConnectService.rejectSession(id);
      } catch (error) {
        console.error('Error rejecting WalletConnect session:', error);
      }
    }
    
    setPendingConnection(null);
  };
  
  // Disconnect from a dApp
  const disconnectFromDApp = async (url) => {
    const connection = activeConnections.find(conn => conn.url === url);
    
    if (!connection) {
      console.error(`No connection found for ${url}`);
      return false;
    }
    
    try {
      if (connection.connectionType === 'walletconnect' && connection.topic) {
        // Disconnect WalletConnect session
        await walletConnectService.disconnectSession(connection.topic);
        
        // Remove the connection origin
        setConnectionOrigins(prev => {
          const updated = { ...prev };
          delete updated[connection.topic];
          return updated;
        });
      }
      
      // Remove from active connections
      setActiveConnections(prev => prev.filter(conn => conn.url !== url));
      console.log(`Disconnected from ${url}`);
      
      return true;
    } catch (error) {
      console.error(`Error disconnecting from ${url}:`, error);
      
      // Still remove from active connections to prevent UI showing connected
      setActiveConnections(prev => prev.filter(conn => conn.url !== url));
      
      return false;
    }
  };

  // Inject provider into iframe
  const injectProvider = useCallback((targetWindow, targetUrl) => {
    if (!currentWallet || !currentChain || !targetWindow || !targetUrl) return;
    
    // Check if this site is allowed to connect
    const isConnected = activeConnections.some(conn => conn.url === targetUrl);
    if (!isConnected) {
      console.log(`Not injecting provider for ${targetUrl} - not connected`);
      return;
    }
    
    setIsInjecting(true);
    try {
      // Create the injected provider
      const injectedProvider = injectEthereumProvider(
        currentWallet,
        currentChain,
        provider,
        handleInjectedRequest
      );
      
      injectedProviderRef.current = injectedProvider;
      
      // Inject the provider into the target window
      targetWindow.postMessage({
        type: 'BITDOG_PROVIDER_INJECTION',
        provider: {
          isConnected: true,
          chainId: ethers.toQuantity(currentChain.chainId),
          networkVersion: currentChain.chainId.toString(),
          selectedAddress: currentWallet.address,
          isUnlocked: true
        }
      }, '*');
      
      console.log(`Provider injected for ${targetUrl}`);
    } catch (error) {
      console.error('Provider injection failed:', error);
    } finally {
      setIsInjecting(false);
    }
  }, [currentWallet, currentChain, provider, activeConnections]);
  
  // Handle requests from injected provider
  const handleInjectedRequest = (requestEvent) => {
    console.log('Injected provider request:', requestEvent);
    
    // Determine request type
    const { method, params, origin } = requestEvent;
    
    // Group methods by type
    const transactionMethods = [
      "eth_sendTransaction",
      "eth_signTransaction",
      "wallet_sendCalls"
    ];
    const signatureMethods = ['eth_sign', 'personal_sign', 'eth_signTypedData', 'eth_signTypedData_v4'];
    const chainMethods = ['wallet_switchEthereumChain', 'wallet_addEthereumChain'];
    
    let requestType = 'other';
    
    if (transactionMethods.includes(method)) {
      requestType = 'transaction';
    } else if (signatureMethods.includes(method)) {
      requestType = 'signature';
    } else if (chainMethods.includes(method)) {
      requestType = 'chainSwitch';
    }
    
    // Auto-handle safe methods
    const safeMethods = [
      'eth_chainId',
      'net_version',
      'eth_blockNumber',
      'eth_getBalance',
      'eth_getCode',
      'eth_getTransactionCount',
      'eth_call',
      'eth_estimateGas',
      'eth_gasPrice',
      'eth_getBlockByNumber',
      'eth_getBlockByHash',
      'eth_getTransactionByHash',
      'eth_getTransactionReceipt'
    ];
    
    if (safeMethods.includes(method)) {
      // Process automatically
      processRequest(requestEvent);
      return;
    }
    
    // Check if this is a permit transaction
    let isPermit = false;
    if (method === 'eth_sendTransaction' && params && params[0] && params[0].data) {
      const data = params[0].data;
      // Use the enhanced permit detection function from walletConnectService
      isPermit = walletConnectService.isPermitTransaction(data);
      
      if (isPermit) {
        console.log('PERMIT TRANSACTION DETECTED in injected request');
      }
    }
    
    // Add to pending requests - browser origin by default, but wallet for permits
    setPendingRequests(prev => [
      ...prev,
      {
        id: requestEvent.id,
        type: requestType,
        method,
        params,
        source: 'injected',
        url: origin || 'Unknown',
        timestamp: Date.now(),
        wcHandlerLocation: isPermit ? 'wallet' : 'browser', // Route permits to wallet
        isPermit
      }
    ]);
  };
  
  // Process request (for automatic handling)
  const processRequest = async (requestEvent) => {
    try {
      let result;
      
      // Process request based on method
      switch (requestEvent.method) {
        case 'eth_chainId':
          result = ethers.toQuantity(currentChain.chainId);
          break;
          
        case 'net_version':
          result = currentChain.chainId.toString();
          break;
          
        case 'eth_blockNumber':
          result = await provider.getBlockNumber();
          result = ethers.toQuantity(result);
          break;
          
        case 'eth_getBalance':
          result = await provider.getBalance(requestEvent.params[0]);
          result = ethers.toQuantity(result);
          break;
          
        // Add other safe methods as needed
          
        default:
          // For other safe methods, forward to the provider
          result = await provider.send(requestEvent.method, requestEvent.params);
      }
      
      // Send response for injected provider
      if (requestEvent.source === 'injected' && injectedProviderRef.current) {
        injectedProviderRef.current.emit('response', {
          id: requestEvent.id,
          result,
          jsonrpc: '2.0'
        });
      }
    } catch (error) {
      console.error(`Error processing ${requestEvent.method} request:`, error);
      
      // Send error response
      const errorResponse = {
        id: requestEvent.id,
        error: {
          code: -32603,
          message: error.message || 'Internal error'
        },
        jsonrpc: '2.0'
      };
      
      if (requestEvent.source === 'injected' && injectedProviderRef.current) {
        injectedProviderRef.current.emit('response', errorResponse);
      }
    }
  };
  
  // Approve a request (transaction, signature, etc.)
  const approveRequest = async (requestId, result) => {
    const requestIndex = pendingRequests.findIndex(req => req.id === requestId);
    if (requestIndex === -1) return false;

    try {
      const request = pendingRequests[requestIndex];
      
      // Check if this is a permit or post-permit swap transaction
      const isPermit = request.isPermit === true;
      const isPostPermitSwap = request.isPostPermitSwap === true;
      
      console.log(`Approving ${request.type} request with ID ${requestId}`,
        isPermit ? ' [PERMIT]' : (isPostPermitSwap ? ' [POST-PERMIT SWAP]' : ''));
      
      // Ensure result is never undefined
      const safeResult = result === undefined ? null : result;
      
      if (request.source === 'walletconnect') {
        try {
          // Ensure proper format for transaction hashes
          let formattedResult = safeResult;
          
          // For transaction hashes, ensure proper hex format
          if (request.type === 'transaction' && safeResult && typeof safeResult === 'string') {
            // Ensure it has 0x prefix
            formattedResult = safeResult.startsWith('0x') ? safeResult : `0x${safeResult}`;
            
            // Validate hex format
            if (!/^0x[0-9a-fA-F]+$/.test(formattedResult)) {
              console.warn('Invalid hex in result, sanitizing');
              const hexMatch = formattedResult.match(/0x[0-9a-fA-F]+/);
              if (hexMatch) {
                formattedResult = hexMatch[0];
              } else {
                // If no valid hex, use a dummy transaction hash for compatibility
                formattedResult = '0x0000000000000000000000000000000000000000000000000000000000000000';
              }
            }
          }
          
          // Create a properly formatted response
          const response = {
            id: requestId,
            jsonrpc: '2.0',
            result: formattedResult
          };
          
          console.log(`Sending WalletConnect response${isPermit ? ' for PERMIT' : (isPostPermitSwap ? ' for POST-PERMIT SWAP' : '')}:`, response);
          
          await walletConnectService.respondSessionRequest({
            topic: request.topic,
            response
          });
        } catch (wcError) {
          console.error('Error responding to WalletConnect request:', wcError);
          throw wcError;
        }
      } else if (request.source === 'injected' && injectedProviderRef.current) {
        // Handle injected provider requests
        injectedProviderRef.current.emit('response', {
          id: requestId,
          result: safeResult,
          jsonrpc: '2.0'
        });
      }
      
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(req => req.id !== requestId));
      return true;
    } catch (error) {
      console.error('Error approving request:', error);
      return false;
    }
  };
  
  // Reject a request
  const rejectRequest = (requestId) => {
    const requestIndex = pendingRequests.findIndex(req => req.id === requestId);
    if (requestIndex === -1) return;
    
    const request = pendingRequests[requestIndex];
    
    // Error response for rejection
    const errorResponse = {
      code: 4001, // User rejected request
      message: 'User rejected the request'
    };
    
    try {
      if (request.source === 'walletconnect') {
        // Handle WalletConnect request
        walletConnectService.rejectRequest(requestId, 'USER_REJECTED');
      } else if (request.source === 'injected' && injectedProviderRef.current) {
        // Handle injected provider request
        injectedProviderRef.current.emit('response', {
          id: requestId,
          error: errorResponse,
          jsonrpc: '2.0'
        });
      }
      
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };
  
  return (
    <DAppContext.Provider
      value={{
        activeConnections,
        pendingConnection,
        pendingRequests,
        isInjecting,
        isWalletKitInitialized,
        connectionOrigins,
        connectToDApp,
        connectWithWalletConnectUri,
        approveConnection,
        rejectConnection,
        disconnectFromDApp,
        injectProvider,
        approveRequest,
        rejectRequest,
        approveAuthentication,
        rejectAuthentication
      }}
    >
      {children}
    </DAppContext.Provider>
  );
};

export default DAppProvider;